<script setup lang="ts">
import WrIcon from '~/components/global/WrIcon.vue';
import { useColorMode } from "#imports";

const { $twMerge } = useNuxtApp();
const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");
const isDark = computed(() => colorMode.value === "dark");

interface BenefitCard {
  title: string;
  description: string;
  icon?: string;
  svgIcon?: string;
}

withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    cards: BenefitCard[];
    /** Extra Tailwind classes merged onto the card grid (e.g. "lg:grid-cols-3 max-w-5xl"). */
    gridClass?: string;
    /** Card background, dark mode only (any CSS value). Empty = default. */
    cardBgDark?: string;
    /** Title text color, dark mode only (any CSS color). Empty = default. */
    titleColorDark?: string;
    /** Description text color, dark mode only (any CSS color). Empty = default. */
    descColorDark?: string;
  }>(),
  {
    heading: '',
    subcopy: '',
    gridClass: '',
    cardBgDark: '',
    titleColorDark: '',
    descColorDark: '',
  }
);
</script>

<template>
  <div class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px] text-center">
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight px-4 font-avant-demi"
      style="background: linear-gradient(92deg, #12DBFF -3.57%, #BA55FE 80.72%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent;"
    >
      {{ heading }}
    </h2>
    <p
      class="text-center mt-4 px-4 text-[16px] lg:text-[18px] lg:opacity-70 font-avant font-normal tracking-[0.36px]"
      :class="isDark ? 'text-[#D9D9D9]' : 'text-black'"
    >
      {{ subcopy }}
    </p>

    <!-- Benefit Cards Grid -->
    <div
      :class="$twMerge('grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mt-10 px-4 max-w-[850px] mx-auto', gridClass)"
    >
      <div
        v-for="(card, idx) in cards"
        :key="idx"
        :class="[
          'benefit-card flex gap-4 items-start max-sm:items-center text-left',
          isDark ? 'dark-border' : 'light-border',
        ]"
        :style="isDark && cardBgDark ? { background: cardBgDark } : undefined"
      >
        <!-- Icon with gradient container (only when an icon or slot is provided) -->
        <div
          v-if="card.svgIcon || card.icon || $slots[`icon-${idx}`]"
          :class="[
            'icon-container flex items-center justify-center flex-shrink-0',
            isLight ? 'light-icon-bg' : 'dark-icon-bg',
          ]"
        >
          <slot :name="`icon-${idx}`" :index="idx">
            <WrIcon
              v-if="card.svgIcon"
              :name="card.svgIcon"
              class="size-6 max-sm:size-5"
              :class="isLight ? 'text-[#501D71]' : 'text-white'"
            />
            <WrIcon
              v-else-if="card.icon"
              :name="card.icon"
              class="size-6 max-sm:size-5"
              :class="isLight ? 'text-[#501D71]' : 'text-[#CDA0FE]'"
            />
          </slot>
        </div>
        <!-- Text -->
        <div class="flex-1">
          <h3
            class="m-0 text-left font-avant"
            :class="isDark ? 'text-[#D9D9D9]' : 'text-black'"
            :style="isDark && titleColorDark ? { color: titleColorDark } : undefined"
            style="font-size: clamp(16px, 2.5vw, 20px); font-weight: 400; line-height: 1.3;"
          >
            <span v-html="card.title"></span>
          </h3>
          <p
            class="mt-3 text-left font-avant"
            :class="isDark ? 'text-[#AF66E4]' : 'text-[#501D71]'"
            :style="isDark && descColorDark ? { color: descColorDark } : undefined"
            style="font-size: clamp(14px, 2vw, 15px); font-weight: 400; letter-spacing: 0.3px;"
          >
            {{ card.description }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.benefit-card {
  border-radius: 12px;
  padding: 20px 24px;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  position: relative;
  overflow: hidden;
}

.benefit-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #12dbff, #ba55fe);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.benefit-card:hover::before {
  opacity: 1;
}

.benefit-card:hover {
  transform: translateY(-3px);
}

/* Light mode */
.benefit-card.light-border {
  border: 1px solid rgba(208, 208, 208, 0.5);
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.benefit-card.light-border:hover {
  box-shadow: 0 8px 24px rgba(166, 85, 246, 0.15);
  border-color: rgba(186, 85, 254, 0.35);
}

/* Dark mode */
.benefit-card.dark-border {
  border: 1px solid rgba(106, 50, 170, 0.25);
  background: linear-gradient(135deg, rgba(1, 3, 20, 0.6) 0%, rgba(43, 1, 82, 0.4) 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.benefit-card.dark-border:hover {
  box-shadow: 0 8px 32px rgba(166, 85, 246, 0.2), 0 0 0 1px rgba(186, 85, 254, 0.15);
  border-color: rgba(186, 85, 254, 0.4);
}

/* Icon container with gradient */
.icon-container {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.icon-container:hover {
  transform: scale(1.05);
}

.light-icon-bg {
  background: linear-gradient(135deg, rgba(18, 219, 255, 0.15) 0%, rgba(186, 85, 254, 0.2) 100%);
  color: #501d71;
}

.light-icon-bg .i {
  color: #501d71;
}

.dark-icon-bg {
  background: linear-gradient(135deg, rgba(18, 219, 255, 0.15) 0%, rgba(186, 85, 254, 0.2) 100%);
  color: #CDA0FE;
}

.dark-icon-bg .i {
  color: #CDA0FE;
}
</style>
